/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext, useMemo } from "react";
import { Filter, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../components/Pagination";
import { AdminContext } from "../../../context/AdminContext";
import { AppContext } from "../../../context/AppContext";
import {
  BookingsTable,
  BookingFilters,
  BookingModal,
  HistoryModal,
} from "../../../components/bookings";

const Bookings = () => {
  const navigate = useNavigate();
  const {
    aToken,
    bookings: rawBookings,
    updateBookingStatus,
    getAllBookings,
    bookingsPagination,
    getBookingHistory,
  } = useContext(AdminContext);
  const { slotDateFormat, calculateAge, currency } = useContext(AppContext);

  // State for filters
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState(""); // Separate input state
  const [statusFilter, setStatusFilter] = useState("All");
  const [showFilters, setShowFilters] = useState(true);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);

  // Modal State
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [reminder, setReminder] = useState("");

  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Status change loading state
  const [statusChanging, setStatusChanging] = useState(false);
  const [currentAction, setCurrentAction] = useState("");

  // Search handlers
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Fetch bookings when filters or pagination changes
  useEffect(() => {
    const fetchBookings = async () => {
      if (aToken) {
        setLoading(true);
        await getAllBookings({
          page: currentPage,
          limit: itemsPerPage,
          status: statusFilter,
          search: searchTerm,
        });
        setLoading(false);
      }
    };

    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(fetchBookings, 300);
    return () => clearTimeout(timeoutId);
  }, [aToken, currentPage, itemsPerPage, statusFilter, searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Transform bookings for display
  const bookings = useMemo(() => {
    return rawBookings.map((appt) => ({
      id: appt.id,
      userId: appt.userId,
      category: appt.category,
      client: {
        name: appt.userData?.name || "Unknown Client",
        phone: appt.userData?.phone || "",
        avatar: appt.userData?.image || null,
        age: appt.userData?.dob ? calculateAge(appt.userData.dob) : "N/A",
      },
      caregiver: {
        id: appt.careGiverData?.id || "",
        name: appt.careGiverData?.name || "Unknown Caregiver",
        avatar: appt.careGiverData?.image || null,
      },
      serviceType: appt.careGiverData?.category || "Unknown",
      dateTime: `${slotDateFormat(appt.slotDate)}, ${appt.slotTime}`,
      salary: appt.amount,
      status: appt.status,
    }));
  }, [rawBookings, slotDateFormat, calculateAge]);

  const handleStatusChange = async (newStatus) => {
    if (!selectedBooking || statusChanging) return;

    setStatusChanging(true);
    setCurrentAction(newStatus);

    try {
      await updateBookingStatus(selectedBooking.id, newStatus, reminder);
      await getAllBookings({
        page: currentPage,
        limit: itemsPerPage,
        status: statusFilter,
        search: searchTerm,
      });

      setReminder("");
      setShowModal(false);
    } catch (error) {
      console.error("Status change failed:", error);
    } finally {
      setStatusChanging(false);
      setCurrentAction("");
    }
  };

  // Load history when opening history modal
  const handleOpenHistoryModal = async (booking) => {
    setSelectedBooking(booking);
    setShowHistoryModal(true);
    setHistoryLoading(true);

    const historyData = await getBookingHistory(booking.id);
    setHistory(historyData);
    setHistoryLoading(false);
  };

  // Close history modal and reset history
  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setHistory([]);
    setHistoryLoading(false);
  };

  // Close update modal and reset states
  const handleCloseUpdateModal = () => {
    setShowModal(false);
    setReminder("");
    setStatusChanging(false);
    setCurrentAction("");
  };

  // Handle update booking click
  const handleUpdateBooking = (booking) => {
    setSelectedBooking(booking);
    setShowModal(true);
  };

  // Handle replace caregiver click
  const handleReplaceCaregiver = () => {
    if (!selectedBooking) return;
    handleCloseUpdateModal();
    navigate(`/bookings/${selectedBooking.id}/replace`, {
      state: {
        selectedBooking: rawBookings.find((a) => a.id === selectedBooking.id),
      },
    });
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setSearchInput("");
    setStatusFilter("All");
    setCurrentPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "New":
        return "bg-blue-600 text-white dark:bg-blue-700 dark:text-white border border-blue-700 dark:border-blue-800";
      case "In Progress":
        return "bg-yellow-600 text-white dark:bg-yellow-700 dark:text-white border border-yellow-700 dark:border-yellow-800";
      case "Work Started":
        return "bg-purple-600 text-white dark:bg-purple-700 dark:text-white border border-purple-700 dark:border-purple-800";
      case "Completed":
        return "bg-emerald-600 text-white dark:bg-emerald-700 dark:text-white border border-emerald-700 dark:border-emerald-800";
      case "Cancelled":
        return "bg-red-600 text-white dark:bg-red-700 dark:text-white border border-red-700 dark:border-red-800";
      default:
        return "bg-gray-600 text-white dark:bg-gray-700 dark:text-white border border-gray-700 dark:border-gray-800";
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-text dark:text-dark-text">
            Bookings
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1">
            Manage client bookings and workflow status.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleResetFilters}
            className="bg-card dark:bg-dark-surface text-gray-600 dark:text-dark-text px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-border text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm flex items-center gap-2"
          >
            <RefreshCw size={16} /> Reset
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="bg-card dark:bg-dark-surface text-gray-600 dark:text-dark-text px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-border text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm flex items-center gap-2"
          >
            <Filter size={16} /> {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <BookingFilters
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        searchTerm={searchTerm}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        handleSearch={handleSearch}
        handleSearchKeyPress={handleSearchKeyPress}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        showStatusDropdown={showStatusDropdown}
        setShowStatusDropdown={setShowStatusDropdown}
        onResetFilters={handleResetFilters}
      />

      {/* Table */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap">
            <thead>
              <tr className="border-b border-slate-700 bg-slate-900">
                <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase tracking-wider">
                  Caregiver
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase tracking-wider">
                  Salary
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-white/70 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              <BookingsTable
                bookings={bookings}
                loading={loading}
                getStatusColor={getStatusColor}
                currency={currency}
                onViewHistory={handleOpenHistoryModal}
                onUpdateBooking={handleUpdateBooking}
              />
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-gray-800/30 p-2">
          <Pagination
            currentPage={currentPage}
            totalPages={bookingsPagination?.totalPages ?? 1}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            totalItems={bookingsPagination?.total || 0}
            loading={loading}
          />
        </div>
      </div>

      {/* Modals */}
      <BookingModal
        selectedBooking={selectedBooking}
        showModal={showModal}
        statusChanging={statusChanging}
        currentAction={currentAction}
        reminder={reminder}
        setReminder={setReminder}
        getStatusColor={getStatusColor}
        currency={currency}
        onClose={handleCloseUpdateModal}
        onStatusChange={handleStatusChange}
        onReplaceCaregiver={handleReplaceCaregiver}
      />

      <HistoryModal
        showHistoryModal={showHistoryModal}
        history={history}
        historyLoading={historyLoading}
        getStatusColor={getStatusColor}
        onClose={handleCloseHistoryModal}
      />
    </div>
  );
};

export default Bookings;
